import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { RequireAuth } from '../auth/RequireAuth';
import { PortalProviders } from '@/portal/PortalProviders';
import { lazyLoadWithRetry } from '@/utils/lazyLoad';


// Eager load Login since it's the entry point (/ redirects to /login)
import Login from "@/pages/Login";

// Lazy load other pages
const NotFound = lazyLoadWithRetry(() => import("@/pages/NotFound"), "NotFound");
const Welcome = lazyLoadWithRetry(() => import("@/pages/Welcome"), "Welcome");
const ForgotPassword = lazyLoadWithRetry(() => import("@/pages/ForgotPassword"), "ForgotPassword");
const Signup = lazyLoadWithRetry(() => import("@/pages/Signup"), "Signup");
const Onboarding = lazyLoadWithRetry(() => import("@/pages/Onboarding"), "Onboarding");
const PartialOnboarding = lazyLoadWithRetry(() => import("@/pages/PartialOnboarding"), "PartialOnboarding");
const Portal = lazyLoadWithRetry(() => import("@/pages/Portal"), "Portal");

// Portal module pages
const PortalMessages = lazyLoadWithRetry(
  () => import("@/portal/pages/MessagesPage").then((m) => ({ default: m.MessagesPage })),
  "PortalMessages"
);
const PortalAccount = lazyLoadWithRetry(
  () => import("@/portal/pages/AccountPage").then((m) => ({ default: m.AccountPage })),
  "PortalAccount"
);
const PortalMeSettings = lazyLoadWithRetry(
  () => import("@/portal/pages/MeSettingsPage").then((m) => ({ default: m.MeSettingsPage })),
  "PortalMeSettings"
);
const PortalTasksSettings = lazyLoadWithRetry(
  () => import("@/portal/pages/TasksSettingsPage").then((m) => ({ default: m.TasksSettingsPage })),
  "PortalTasksSettings"
);
const PortalUsers = lazyLoadWithRetry(
  () => import("@/portal/pages/UsersPage"),
  "PortalUsers"
);
const PortalSignatures = lazyLoadWithRetry(
  () => import("@/portal/pages/SignaturesPage").then((m) => ({ default: m.SignaturesPage })),
  "PortalSignatures"
);
const PortalCreditRequest = lazyLoadWithRetry(
  () => import("@/portal/pages/CreditRequestPage").then((m) => ({ default: m.CreditRequestPage })),
  "PortalCreditRequest"
);
const PortalNotifications = lazyLoadWithRetry(
  () => import("@/portal/pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })),
  "PortalNotifications"
);
const PortalOnboardingStatus = lazyLoadWithRetry(
  () => import("@/portal/pages/OnboardingStatusPage").then((m) => ({ default: m.OnboardingStatusPage })),
  "PortalOnboardingStatus"
);
const PortalResolveBlockers = lazyLoadWithRetry(
  () => import("@/portal/pages/ResolveBlockersPage").then((m) => ({ default: m.ResolveBlockersPage })),
  "PortalResolveBlockers"
);
const PortalFinancials = lazyLoadWithRetry(
  () => import("@/portal/pages/FinancialsPage").then((m) => ({ default: m.FinancialsPage })),
  "PortalFinancials"
);
const PortalMoney = lazyLoadWithRetry(
  () => import("@/portal/pages/MoneyPage").then((m) => ({ default: m.MoneyPage })),
  "PortalMoney"
);
const PortalCampaigns = lazyLoadWithRetry(
  () => import("@/portal/pages/CampaignsPage").then((m) => ({ default: m.CampaignsPage })),
  "PortalCampaigns"
);
const PortalSupport = lazyLoadWithRetry(
  () => import("@/portal/pages/SupportPage").then((m) => ({ default: m.SupportPage })),
  "PortalSupport"
);
const PortalPartnerCompliance = lazyLoadWithRetry(
  () => import("@/portal/pages/PartnerCompliancePage").then((m) => ({ default: m.PartnerCompliancePage })),
  "PortalPartnerCompliance"
);

// Helpdesk module pages
const PortalMyTickets = lazyLoadWithRetry(
  () => import("@/helpdesk/pages/portal/MyTicketsPage").then((m) => ({ default: m.MyTicketsPage })),
  "PortalMyTickets"
);
const PortalTicketDetail = lazyLoadWithRetry(
  () => import("@/helpdesk/pages/portal/TicketDetailPage").then((m) => ({ default: m.PortalTicketDetailPage })),
  "PortalTicketDetail"
);
const PortalKnowledgeBase = lazyLoadWithRetry(
  () => import("@/helpdesk/pages/portal/KnowledgeBasePortalPage").then((m) => ({ default: m.KnowledgeBasePortalPage })),
  "PortalKnowledgeBase"
);
const ProfileSettings = lazyLoadWithRetry(
  () => import("@/pages/ProfileSettings").then((m) => ({ default: m.ProfileSettings })),
  "ProfileSettings",
);
const AdminRoutes = lazyLoadWithRetry(
  () => import("@/pages/admin/AdminRoutes").then((m) => ({ default: m.AdminRoutes })),
  "AdminRoutes",
);
const PublicDSRRequest = lazyLoadWithRetry(
  () => import("@/pages/PublicDSRRequest").then((m) => ({ default: m.PublicDSRRequest })),
  "PublicDSRRequest",
);
const RelationshipAccept = lazyLoadWithRetry(
  () => import("@/pages/RelationshipAccept"),
  "RelationshipAccept",
);
const SigningDelegation = lazyLoadWithRetry(
  () => import("@/pages/SigningDelegation"),
  "SigningDelegation",
);
const BillingTransfer = lazyLoadWithRetry(
  () => import("@/pages/BillingTransfer"),
  "BillingTransfer",
);
const Unauthorized = lazyLoadWithRetry(
  () => import("@/pages/Unauthorized"),
  "Unauthorized",
);
const CreditAgreementPage = lazyLoadWithRetry(
  () => import("@/pages/CreditAgreementPage"),
  "CreditAgreementPage",
);
const AgentOnboarding = lazyLoadWithRetry(
  () => import("@/pages/AgentOnboarding"),
  "AgentOnboarding",
);
const AgentClientOnboarding = lazyLoadWithRetry(
  () => import("@/pages/AgentClientOnboarding"),
  "AgentClientOnboarding",
);
const ApiDocs = lazyLoadWithRetry(
  () => import("@/pages/ApiDocs"),
  "ApiDocs",
);
const SalesforceCallback = lazyLoadWithRetry(
  () => import("@/pages/oauth/SalesforceCallback"),
  "SalesforceCallback",
);
const ConfirmGdprConsent = lazyLoadWithRetry(
  () => import("@/pages/ConfirmGdprConsent"),
  "ConfirmGdprConsent",
);
const SignDocumentPage = lazyLoadWithRetry(
  () => import("@/pages/SignDocumentPage"),
  "SignDocumentPage",
);
const SignatoryAcceptPage = lazyLoadWithRetry(
  () => import("@/pages/SignatoryAcceptPage"),
  "SignatoryAcceptPage",
);
const EverboardingTaskPage = lazyLoadWithRetry(
  () => import("@/pages/EverboardingTaskPage"),
  "EverboardingTaskPage",
);

// Module entry points — template literals prevent TS from following the import chain
// into the legacy CMMS module (which has its own, looser type conventions).
const CMMSEntry = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../cmms/src/CMMSEntry"),
  "CMMSEntry",
);
const AssetManagementEntry = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../cmms/src/AssetManagementEntry"),
  "AssetManagementEntry",
);
const DMSEntry = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../cmms/src/DMSEntry"),
  "DMSEntry",
);
const AnalyticsEntry = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../cmms/src/AnalyticsEntry"),
  "AnalyticsEntry",
);
const SiteDevModule = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../site-dev/pages/SiteDevModule"),
  "SiteDevModule",
);
const CommsHubEntry = lazyLoadWithRetry(
  () => import(/* @vite-ignore */ "../../communication-hub/CommsHubEntry"),
  "CommsHubEntry",
);

// Signature module pages
const SignaturesLayout = lazyLoadWithRetry(
  () => import("@/signatures/components/layout/SignaturesLayout").then((m) => ({ default: m.SignaturesLayout })),
  "SignaturesLayout",
);
const SignaturesHome = lazyLoadWithRetry(
  () => import("@/signatures/pages/SignaturesHome").then((m) => ({ default: m.SignaturesHome })),
  "SignaturesHome",
);
const CreateRequestPage = lazyLoadWithRetry(
  () => import("@/signatures/pages/CreateRequestPage").then((m) => ({ default: m.CreateRequestPage })),
  "CreateRequestPage",
);
const RequestDetailPage = lazyLoadWithRetry(
  () => import("@/signatures/pages/RequestDetailPage").then((m) => ({ default: m.RequestDetailPage })),
  "RequestDetailPage",
);
const TemplatesPage = lazyLoadWithRetry(
  () => import("@/signatures/pages/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
  "TemplatesPage",
);
const SignatureSettingsPage = lazyLoadWithRetry(
  () => import("@/signatures/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
  "SignatureSettingsPage",
);

// Page loader
const PageLoader = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// Page transition wrapper
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94],
    }}
  >
    {children}
  </motion.div>
);

// Portal route wrapper for consistency
const PortalRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAuth requireOnboardingComplete>
    <PortalProviders>
      <PageWrapper>{children}</PageWrapper>
    </PortalProviders>
  </RequireAuth>
);

export const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
      <Suspense fallback={<PageLoader />}>
        <Routes location={location}>
          {/* Public routes */}
          <Route path="/welcome" element={<PageWrapper><Welcome /></PageWrapper>} />
          <Route path="/login" element={<PageWrapper><Login /></PageWrapper>} />
          <Route path="/forgot-password" element={<PageWrapper><ForgotPassword /></PageWrapper>} />
          <Route path="/signup" element={<PageWrapper><Signup /></PageWrapper>} />
          <Route path="/gdpr/request" element={<PageWrapper><PublicDSRRequest /></PageWrapper>} />
          <Route path="/api-docs" element={<PageWrapper><ApiDocs /></PageWrapper>} />
          <Route path="/oauth/salesforce/callback" element={<PageWrapper><SalesforceCallback /></PageWrapper>} />
          <Route path="/relationship/accept" element={<PageWrapper><RelationshipAccept /></PageWrapper>} />
          <Route path="/delegation/signing" element={<PageWrapper><SigningDelegation /></PageWrapper>} />
          <Route path="/billing/transfer" element={<PageWrapper><BillingTransfer /></PageWrapper>} />
          <Route path="/credit-agreement/:token" element={<PageWrapper><CreditAgreementPage /></PageWrapper>} />
          <Route path="/sign/:token" element={<PageWrapper><SignDocumentPage /></PageWrapper>} />
          <Route path="/signatory/accept/:token" element={<PageWrapper><SignatoryAcceptPage /></PageWrapper>} />
          <Route path="/everboarding" element={<PageWrapper><EverboardingTaskPage /></PageWrapper>} />
          <Route path="/confirm-gdpr" element={<PageWrapper><ConfirmGdprConsent /></PageWrapper>} />

          {/* Protected routes */}
          <Route path="/onboarding" element={<PageWrapper><Onboarding /></PageWrapper>} />
          <Route path="/onboarding/agent" element={<PageWrapper><AgentOnboarding /></PageWrapper>} />
          <Route path="/onboarding/agent-client" element={<PageWrapper><AgentClientOnboarding /></PageWrapper>} />
          <Route path="/onboarding/supplier" element={<PageWrapper><Onboarding /></PageWrapper>} />
          <Route path="/onboarding/partial" element={<PageWrapper><PartialOnboarding /></PageWrapper>} />
          <Route path="/profile-settings" element={
            <RequireAuth>
              <PageWrapper><ProfileSettings /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* Portal Routes - New Structure */}
          <Route path="/portal" element={<PortalRoute><Portal /></PortalRoute>} />
          
          {/* Campaigns */}
          <Route path="/portal/campaigns" element={<PortalRoute><PortalCampaigns /></PortalRoute>} />
          <Route path="/portal/campaigns/*" element={<PortalRoute><PortalCampaigns /></PortalRoute>} />
          
          {/* Money (unified financial documents) */}
          <Route path="/portal/money" element={<PortalRoute><PortalMoney /></PortalRoute>} />
          <Route path="/portal/money/*" element={<PortalRoute><PortalMoney /></PortalRoute>} />
          
          {/* Legacy Financials redirects */}
          <Route path="/portal/financials" element={<Navigate to="/portal/money" replace />} />
          <Route path="/portal/financials/*" element={<Navigate to="/portal/money" replace />} />
          <Route path="/portal/billing" element={<Navigate to="/portal/money" replace />} />
          
          {/* Settings (simplified 4-pillar navigation: Account, Me, Team, Tasks) */}
          <Route path="/portal/settings" element={<PortalRoute><PortalAccount /></PortalRoute>} />
          <Route path="/portal/settings/me" element={<PortalRoute><PortalMeSettings /></PortalRoute>} />
          <Route path="/portal/settings/team" element={<PortalRoute><PortalUsers /></PortalRoute>} />
          <Route path="/portal/settings/tasks" element={<PortalRoute><PortalTasksSettings /></PortalRoute>} />
          
          {/* Legacy routes redirect to new consolidated structure */}
          <Route path="/portal/settings/organization" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/settings/profile" element={<Navigate to="/portal/settings/me" replace />} />
          <Route path="/portal/settings/notifications" element={<Navigate to="/portal/settings/me" replace />} />
          <Route path="/portal/settings/security" element={<Navigate to="/portal/settings/me" replace />} />
          <Route path="/portal/settings/brands" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/settings/compliance" element={<Navigate to="/portal/settings/tasks" replace />} />
          <Route path="/portal/settings/documents" element={<Navigate to="/portal/settings/tasks" replace />} />
          <Route path="/portal/settings/preferences" element={<Navigate to="/portal/settings/me" replace />} />
          
          {/* Legacy Account section redirects */}
          <Route path="/portal/account" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/account/organization" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/account/team" element={<Navigate to="/portal/settings/team" replace />} />
          <Route path="/portal/account/brands" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/account/compliance" element={<Navigate to="/portal/settings/tasks" replace />} />
          <Route path="/portal/account/documents" element={<Navigate to="/portal/settings/tasks" replace />} />
          <Route path="/portal/users" element={<Navigate to="/portal/settings/team" replace />} />
          <Route path="/portal/brands" element={<Navigate to="/portal/settings" replace />} />
          <Route path="/portal/compliance" element={<Navigate to="/portal/settings/tasks" replace />} />
          <Route path="/portal/documents" element={<Navigate to="/portal/settings/tasks" replace />} />
          
          {/* Support Section - Work-related items accessible from Work pillar */}
          <Route path="/portal/support" element={<Navigate to="/portal" replace />} />
          <Route path="/portal/support/tickets" element={<PortalRoute><PortalMyTickets /></PortalRoute>} />
          <Route path="/portal/support/tickets/:ticketId" element={<PortalRoute><PortalTicketDetail /></PortalRoute>} />
          <Route path="/portal/support/knowledge-base" element={<PortalRoute><PortalKnowledgeBase /></PortalRoute>} />
          <Route path="/portal/support/messages" element={<PortalRoute><PortalMessages /></PortalRoute>} />
          <Route path="/portal/support/signatures" element={<PortalRoute><PortalSignatures /></PortalRoute>} />
          <Route path="/portal/support/settings" element={<Navigate to="/portal/settings/profile" replace />} />
          
          {/* Legacy redirects for Support section */}
          <Route path="/portal/messages" element={<Navigate to="/portal/support/messages" replace />} />
          <Route path="/portal/signatures" element={<Navigate to="/portal/support/signatures" replace />} />
          <Route path="/portal/settings" element={<Navigate to="/portal/settings" replace />} />
          
          {/* Other Portal Routes (keep for utility pages) */}
          <Route path="/portal/credit/request" element={<PortalRoute><PortalCreditRequest /></PortalRoute>} />
          <Route path="/portal/notifications" element={<PortalRoute><PortalNotifications /></PortalRoute>} />
          <Route path="/portal/onboarding-status" element={<PortalRoute><PortalOnboardingStatus /></PortalRoute>} />
          <Route path="/portal/resolve-blockers" element={<PortalRoute><PortalResolveBlockers /></PortalRoute>} />
          
          {/* Partner Compliance */}
          <Route path="/portal/partner-compliance" element={<PortalRoute><PortalPartnerCompliance /></PortalRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <RequireAuth allowedRoles={['super_admin', 'platform_admin']}>
              <AdminRoutes />
            </RequireAuth>
          } />
          
          {/* Signatures module routes */}
          <Route path="/signatures" element={
            <RequireAuth>
              <PageWrapper><SignaturesLayout><SignaturesHome /></SignaturesLayout></PageWrapper>
            </RequireAuth>
          } />
          <Route path="/signatures/create" element={
            <RequireAuth>
              <PageWrapper><SignaturesLayout><CreateRequestPage /></SignaturesLayout></PageWrapper>
            </RequireAuth>
          } />
          <Route path="/signatures/:requestId" element={
            <RequireAuth>
              <PageWrapper><SignaturesLayout><RequestDetailPage /></SignaturesLayout></PageWrapper>
            </RequireAuth>
          } />
          <Route path="/signatures/templates" element={
            <RequireAuth>
              <PageWrapper><SignaturesLayout><TemplatesPage /></SignaturesLayout></PageWrapper>
            </RequireAuth>
          } />
          <Route path="/signatures/settings" element={
            <RequireAuth>
              <PageWrapper><SignaturesLayout><SignatureSettingsPage /></SignaturesLayout></PageWrapper>
            </RequireAuth>
          } />
          
          {/* Asset Management Module */}
          <Route path="/assets/*" element={
            <RequireAuth>
              <PageWrapper><AssetManagementEntry /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* CMMS Module */}
          <Route path="/cmms/*" element={
            <RequireAuth>
              <PageWrapper><CMMSEntry /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* DMS Module */}
          <Route path="/dms/*" element={
            <RequireAuth>
              <PageWrapper><DMSEntry /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* Analytics Module */}
          <Route path="/analytics/*" element={
            <RequireAuth>
              <PageWrapper><AnalyticsEntry /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* Communications Hub Module */}
          <Route path="/comms/*" element={
            <RequireAuth>
              <PageWrapper><CommsHubEntry /></PageWrapper>
            </RequireAuth>
          } />

          {/* Site Development Module */}
          <Route path="/site-dev/*" element={
            <RequireAuth>
              <PageWrapper><SiteDevModule /></PageWrapper>
            </RequireAuth>
          } />
          
          {/* Unauthorized access page */}
          <Route path="/unauthorized" element={<PageWrapper><Unauthorized /></PageWrapper>} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 */}
          <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
        </Routes>
      </Suspense>
  );
};
